import { fabric } from 'fabric';
import { type Diagram, generateDiagram } from '@aha-app/diagrams';
import CollaborationBehaviors from './CollaborationBehaviors';
import Shape from './Shape';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import AnalyticsBehaviors from './AnalyticsBehaviors';
import ReadOnlyBehaviors from './ReadOnlyBehaviors';
import FrameMemberBehaviors from './FrameMemberBehaviors';
import LockBehaviors from './LockBehaviors';
import tools from '../tools';
import Serialization, { type DocData } from '../collaboration/Serialization';
import DataStore from '../collaboration/DataStore';
import ShapeBase from '../shapes/ShapeBase';
import { type ShapeName } from '../shapes';
import type Connector from './Connector';
import type WhiteboardController from '../controllers/WhiteboardController';

export const smartObjectsEnabled = () =>
  !!window.featureFlags?.create_1552_smart_shapes;

export const toWhiteboardData = async (
  controller: WhiteboardController,
  newDiagram: Diagram
): Promise<DocData> => {
  const measureShape = new Shape({
    controller,
    shapeName: 'rect',
  });

  const whiteboard = await newDiagram.toWhiteboard({
    measureTextFunc: async (text, options) => {
      measureShape.set({
        shapeName: (options.shapeName || 'rect') as ShapeName,
        textContent: text,
        width: options.containerWidth,
      });

      let fontSize: number | null = null;
      if (measureShape.textProps.fontSize === 'scaleToFit') {
        fontSize = await measureShape.shrinkText();
      }

      const dimensions = await measureShape.measureTextDimensions();

      return {
        width: Math.max(dimensions.width, 20),
        height: Math.max(dimensions.height, 20),
        fontSize: fontSize ?? measureShape.fontSize ?? 14,
      };
    },
  });

  // add z-indexes. we assume the object values will be in z-order, but ideally
  // the diagram generation would provide z indexes or strongly-ordered data (e.g. an array)
  Object.values(whiteboard.objects).forEach((o, i) => {
    o.z = i * 10;
  });

  return whiteboard;
};

type ChildObject = Connector | Shape;

// An object that generates a diagram from a textual input.
interface SmartObject
  extends fabric.Object,
    LockBehaviors,
    ReadOnlyBehaviors,
    CollaborationBehaviors,
    FrameMemberBehaviors {}

@LockBehaviors
@AnalyticsBehaviors
@ReadOnlyBehaviors
@CollaborationBehaviors
@FrameMemberBehaviors
class SmartObject extends fabric.Object {
  type = 'smartObject';
  noScaleCache = false;
  // So edges are sharp on large objects at high scales
  objectCaching = false;

  uniformScaling = true;
  snapAngle = 45;
  snapThreshold = 5;

  // Keys that will be serialized for saving and sending to remote systems.
  serializedKeys = [
    'z',
    'left',
    'top',
    'width',
    'height',
    'scaleX',
    'scaleY',
    'angle',
    'textContent',
  ];

  controls: Record<string, fabric.Control> = {
    tl: ShapeBase.resizeControl({ x: -0.5, y: -0.5 }),
    tr: ShapeBase.resizeControl({ x: 0.5, y: -0.5 }),
    bl: ShapeBase.resizeControl({ x: -0.5, y: 0.5 }),
    br: ShapeBase.resizeControl({ x: 0.5, y: 0.5 }),
    mtr: ShapeBase.rotateControl(),
  };

  objects: ChildObject[] = [];
  clickedObject?: fabric.Object;
  textContent: string;

  // Current diagram internal state.
  diagram: Diagram | null = null;

  // Whether there is an error in the diagram.
  diagramError: string | null = null;

  private _transformDone?: boolean;

  constructor(options) {
    super({
      width: 500,
      height: 500,
      ...options,
    });
    this.initializeFrameMember();
    this.initializeLockBehaviors(options.locked);

    this.textContent ||= '';

    this.on('added', () => {
      void this._textToObjects();
    });
    this.on('scaled', () => {
      void this._textToObjects();
    });
    this.on('deselected', () => {
      this.clickedObject = undefined;
    });

    // Support selecting a sub-object.
    this.on('mousedblclick', () => {
      if (this.locked) return;

      // Open the side panel for editing.
      this.controller.actionOpenSidePanel('smartObject');
    });

    // Use mousedown so the textarea doesn't steal focus from the canvas
    this.on('mousedown', eventData => {
      if (this.locked) return;
      if (!this.isSelected()) return;

      this.selectSubTarget(eventData);
    });
  }

  // See if a sub-object was clicked.
  selectSubTarget(eventData) {
    // Select the object that was double-clicked.
    let pointer = this.canvas.getPointer(eventData.e, true);

    // Translate the pointer to deal with the fact that the objects are
    // centered on this object.
    // @ts-expect-error _normalizePointer does exist just not in the types
    pointer = this.canvas._normalizePointer(this, pointer);
    pointer.x = pointer.x + this.width / 2;
    pointer.y = pointer.y + this.height / 2;

    // Make sure object coords are updated for zoom and translate. We use a
    // a trick here to make the coordinates be relative to this object, like
    // a fabric group does.
    this.objects.forEach(object => {
      // @ts-expect-error not a real group
      object.group = this;
      object.setCoords();
      object.group = undefined;
    });

    const target = this.canvas._searchPossibleTargets(this.objects, pointer);
    if (target) {
      // Track the sub-selection and render specially.
      this.clickedObject = target;

      // Select the corresponding text in the side panel.
      if (this.diagram) {
        const location = this.diagram.getTextSelectionById(target.id);

        if (location) {
          this.controller.dispatchEvent('smartObject.clicked', {
            smartObjectId: this.id,
            location,
          });
        }
      }
    } else {
      this.clickedObject = undefined;
    }
    this.canvas.requestRenderAll();
  }

  _set(keyOrPath: KeyPath, value: any): fabric.Object {
    super._set(keyOrPath, value);

    if (keyOrPath === 'textContent') {
      void this._textToObjects();
    }

    return this;
  }

  // Convert the text representation to objects.
  private async _textToObjects() {
    if (!this.canvas) return;

    // this.makeFlowChart();
    const whiteboardContent = await this.generateDiagram();
    if (whiteboardContent) {
      // TODO: Rather than completely recreating objects, patch them. That
      // would reduce flickering, and could allow cool animating effects as
      // items move around.
      this.objects = [];

      // A version of the controller which uses a separate store from the
      // the main controller. This store has no client and doesn't make
      // collaborative updates. We need a DataStore so that objects can be
      // looked up by ID to handle connectors and labels.
      const noClientStore = new DataStore('objects', null);
      const proxyController = new Proxy(this.controller, {
        get(target, prop, receiver) {
          if (prop === 'dataStoreObjects') {
            return noClientStore;
          } else {
            return Reflect.get(target, prop, receiver);
          }
        },
      });

      const objectMap = {};
      for (const objectData of Object.values(whiteboardContent.objects)) {
        const object = Serialization.restoreObject(
          proxyController,
          objectData
        ) as ChildObject;
        objectMap[object.id] = object;

        this.objects.push(object);
      }

      for (const object of this.objects) {
        object.canvas = this.canvas;
        object.fire('added');
      }
    }

    this.canvas?.requestRenderAll();
  }

  _render(ctx: CanvasRenderingContext2D) {
    ctx.save();
    if (this.diagramError) {
      ctx.save();
      ctx.strokeStyle = 'red';
      ctx.lineWidth = 2;
      ctx.strokeRect(
        -this.width / 2,
        -this.height / 2,
        this.width,
        this.height
      );
      ctx.restore();
    }

    ctx.translate(-this.width / 2, -this.height / 2);

    this._transformDone = true; // Emulate the behviour of fabric.Group
    (this.objects || []).forEach(object => {
      ctx.save();
      object.transform(ctx);
      object.drawObject(ctx);

      if (this.clickedObject === object) {
        // if we want to highlight the clicked object, do it here
      }
      ctx.restore();
    });

    this._transformDone = false;
    ctx.restore();
  }

  tools() {
    if (!smartObjectsEnabled()) {
      return null;
    }
    return [[tools.ToolExplodeSmartObject, tools.ToolEditSmartObject]];
  }

  // Generate whiteboard content as JSON from the current text content.
  async generateDiagram(): Promise<DocData | undefined> {
    const newDiagram = generateDiagram(this.textContent, {
      widthHint: this.width,
      heightHint: this.height,
    });

    const whiteboardJson = await toWhiteboardData(this.controller, newDiagram);

    if (newDiagram.errors.length > 0) {
      this.controller.dispatchEvent('smartObject.error', {
        smartObjectId: this.id,
        errors: newDiagram.errors,
      });
    }

    // If we already have a diagram and the new one is an error, keep the old.
    if (!this.diagram || !newDiagram.isError()) {
      this.diagram = newDiagram;
      this.set({ width: newDiagram.width(), height: newDiagram.height() });

      return whiteboardJson;
    }
  }
}

export default SmartObject;
